import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from "@mui/icons-material";
import { Box, Chip } from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import MicrospecDataTable, { MicrospecDataTableColumn } from "../../../../MicrospecDynamicUI/Components/DataTable/MicrospecFirestoreDataTable";
import { useMicrospecDynamicUIContext } from "../../../../MicrospecDynamicUI/GlobalProvider";
import { deleteDocumentById } from "../../../../MicrospecDynamicUI/Helpers/dataUtils";
import { useMicrospecThemeLanguageContext } from "../../../../MicrospecDynamicUI/Themes";
import { PRODUCTS_COLLECTION, SPECIFICATIONS_COLLECTION } from "../../../constants/collectionPaths";

const typeColorMap: Record<string, 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'info'> = {
    BOOLEAN: 'default',
    NUMBER_RANGE: 'info',
    LIST_OF_VALUES: 'primary',
    COLOR_PICKER: 'secondary',
};

const SpecificationsManagementPage: React.FC = () => {
    const navigate = useNavigate();
    const { translate } = useMicrospecThemeLanguageContext().localizationProvider;
    const { cloudProvider } = useMicrospecDynamicUIContext();
    const { firestore, userProfile } = cloudProvider;
    const { getEntitiesRelationsCount } = userProfile.methods;
    const db = firestore?.instance;
    const t = translate;
    const [refreshKey, setRefreshKey] = useState(0);

    const columns: MicrospecDataTableColumn[] = [
        {
            field: 'name',
            headerName: t('specification.name'),
            sortable: true,
            minWidth: 200,
            align: 'left',
            render: (value: string | { [locale: string]: string }) => typeof value === 'string' ? value : translate(value),
        },
        {
            field: 'type',
            headerName: t('specification.type'),
            sortable: true,
            minWidth: 160,
            align: 'center',
            render: (value: string) => (
                <Chip label={t(`specification.type${value.split('_').map((w: string) => w[0] + w.slice(1).toLowerCase()).join('')}`)} color={typeColorMap[value] || 'default'} size="small" variant="outlined" />
            ),
        },
        {
            field: 'additional_price',
            headerName: t('specification.additionalPrice'),
            sortable: true,
            minWidth: 140,
            align: 'center',
            render: (value: number) => value > 0 ? `+₪${value.toFixed(2)}` : '-',
        },
        {
            field: 'is_available',
            headerName: t('specification.available'),
            sortable: true,
            minWidth: 120,
            align: 'center',
            render: (value: boolean) => (
                <Chip label={value ? t('specification.availableToSelect') : t('specification.unavailable')} color={value ? 'success' : 'warning'} size="small" variant="outlined" />
            ),
        },
    ];

    const handleDelete = async (row: any) => {
        if (!db) return;
        await getEntitiesRelationsCount([
            { parentEntityId: row.id, childEntityCollection: PRODUCTS_COLLECTION, childFieldName: 'specification_ids', queryOperator: 'array-contains' },
        ]).then(async (response: { results: { count: number }[] }) => {
            if (response.results[0].count > 0) {
                alert(t('specification.hasProducts'));
                return;
            }
            if (window.confirm(t('specification.confirmDelete'))) {
                try {
                    await deleteDocumentById(db, SPECIFICATIONS_COLLECTION, row.id);
                    setRefreshKey(prev => prev + 1);
                } catch (error: unknown) {
                    alert(t('specification.deleteFailed'));
                }
            }
        }).catch(() => {
            alert(t('specification.deleteFailed'));
        });
    };

    return (
        <Box sx={{ width: '100%', p: 2 }}>
            <MicrospecDataTable
                key={refreshKey}
                collection={SPECIFICATIONS_COLLECTION}
                columns={columns}
                title={t('common.specifications')}
                subtitle={t('specification.manageDescription')}
                searchable={true}
                exportable={true}
                refreshable={true}
                pageSize={25}
                maxHeight={700}
                onRowClick={(row) => navigate(`/manager/specifications/update/${row.id}`)}
                actions={[
                    { icon: <EditIcon />, label: t('common.edit'), onClick: (row) => navigate(`/manager/specifications/update/${row.id}`) },
                    { icon: <DeleteIcon />, label: t('common.delete'), onClick: handleDelete },
                ]}
                globalActions={[
                    { icon: <AddIcon />, label: t('common.addSpecification'), onClick: () => navigate('/manager/specifications/add'), variant: 'contained', color: 'primary' },
                ]}
            />
        </Box>
    );
};

export default SpecificationsManagementPage;
